import { useEffect, useState } from 'react'
import {
  Box,
  Grid,
  GridItem,
  Heading,
  VStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatNumber,
  StatLabel,
} from '@chakra-ui/react'
import { Users, FileText, Folder, Tag } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { useThemeColors } from '@/hooks/useThemeColors'
import { StatCard, LineChart, PieChart } from '@/components/dashboard'
import { analyticsService, type AnalyticsOverview } from '@/lib/services/analyticsService'

export default function AnalyticsDashboardPage() {
  const colors = useThemeColors()
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const data = await analyticsService.getOverview()
        setOverview(data)
      } catch (error) {
        console.error('Error fetching analytics overview:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchOverview()
  }, [])

  if (!overview) {
    return (
      <AdminLayout>
        <Box p={6}>
          <Text>Loading analytics overview...</Text>
        </Box>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="auto" minH={0}>
          <Card flexShrink={0}>
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Analytics Overview
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Comprehensive analytics and insights about your system
              </Text>
            </CardHeader>

            <CardBody pb={4}>
              <VStack spacing={6} align="stretch">
                <Grid
                  templateColumns={{
                    base: '1fr',
                    md: 'repeat(2, 1fr)',
                    lg: 'repeat(3, 1fr)',
                    xl: 'repeat(5, 1fr)',
                  }}
                  gap={6}
                >
                  <GridItem>
                    <StatCard
                      title="Total Users"
                      value={overview.summary.total_users}
                      icon={Users}
                      color="blue"
                      isLoading={isLoading}
                    />
                  </GridItem>
                  <GridItem>
                    <StatCard
                      title="Total Pages"
                      value={overview.summary.total_pages}
                      icon={FileText}
                      color="green"
                      isLoading={isLoading}
                    />
                  </GridItem>
                  <GridItem>
                    <StatCard
                      title="Media Files"
                      value={overview.summary.total_media}
                      icon={FileText}
                      color="purple"
                      isLoading={isLoading}
                    />
                  </GridItem>
                  <GridItem>
                    <StatCard
                      title="Categories"
                      value={overview.summary.total_categories}
                      icon={Folder}
                      color="orange"
                      isLoading={isLoading}
                    />
                  </GridItem>
                  <GridItem>
                    <StatCard
                      title="Tags"
                      value={overview.summary.total_tags}
                      icon={Tag}
                      color="teal"
                      isLoading={isLoading}
                    />
                  </GridItem>
                </Grid>

                <Box>
                  <Heading size="sm" mb={4}>
                    User Metrics
                  </Heading>
                  <Grid
                    templateColumns={{
                      base: '1fr',
                      md: 'repeat(2, 1fr)',
                      lg: 'repeat(5, 1fr)',
                    }}
                    gap={4}
                  >
                    <Card>
                      <CardBody>
                        <Stat>
                          <StatLabel>Active Users</StatLabel>
                          <StatNumber>{overview.user_metrics.active}</StatNumber>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <Stat>
                          <StatLabel>New Today</StatLabel>
                          <StatNumber>{overview.user_metrics.new_today}</StatNumber>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <Stat>
                          <StatLabel>New This Week</StatLabel>
                          <StatNumber>{overview.user_metrics.new_this_week}</StatNumber>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <Stat>
                          <StatLabel>New This Month</StatLabel>
                          <StatNumber>{overview.user_metrics.new_this_month}</StatNumber>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <Stat>
                          <StatLabel>New Last 30 Days</StatLabel>
                          <StatNumber>{overview.user_metrics.new_last_30_days}</StatNumber>
                        </Stat>
                      </CardBody>
                    </Card>
                  </Grid>
                </Box>

                {overview.trends.user_registration.labels.length > 0 && (
                  <LineChart
                    title="User Registration Trend"
                    labels={overview.trends.user_registration.labels}
                    data={overview.trends.user_registration.data}
                    color="#3182CE"
                    isLoading={isLoading}
                  />
                )}

                <Grid
                  templateColumns={{
                    base: '1fr',
                    md: 'repeat(2, 1fr)',
                  }}
                  gap={6}
                >
                  {overview.trends.page_creation.labels.length > 0 && (
                    <GridItem>
                      <LineChart
                        title="Page Creation Trend"
                        labels={overview.trends.page_creation.labels}
                        data={overview.trends.page_creation.data}
                        color="#38A169"
                        isLoading={isLoading}
                      />
                    </GridItem>
                  )}

                  {Object.keys(overview.activity_metrics.by_module).length > 0 && (
                    <GridItem>
                      <PieChart
                        title="Activity by Module"
                        labels={Object.keys(overview.activity_metrics.by_module)}
                        data={Object.values(overview.activity_metrics.by_module)}
                        isLoading={isLoading}
                      />
                    </GridItem>
                  )}

                  {Object.keys(overview.activity_metrics.by_action).length > 0 && (
                    <GridItem>
                      <PieChart
                        title="Activity by Action"
                        labels={Object.keys(overview.activity_metrics.by_action)}
                        data={Object.values(overview.activity_metrics.by_action)}
                        isLoading={isLoading}
                      />
                    </GridItem>
                  )}
                </Grid>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </AdminLayout>
  )
}

